import { Component, ViewChild, ViewContainerRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService } from '@app/core/http-services/user.service';
import { GlobalErrorHandlerService } from '@app/core/services';
import { ToastService } from '@ceslicensing/toast-notification';

@Component({
  selector: 'app-mfa-preferences',
  templateUrl: './mfa-preferences.component.html',
  styleUrls: ['./mfa-preferences.component.scss'],
})
export class MfaPreferencesComponent {
  destroy$: Subject<boolean> = new Subject<boolean>();
  @ViewChild('container', { read: ViewContainerRef })
  public container: ViewContainerRef;
  loader: boolean = false;
  guardianPushNotificationEnable: boolean = false;
  authenticatorOtpEnable: boolean = false;
  smsEnable: boolean = false;
  emailEnable: boolean = false;
  recoveryCodeEnable: boolean = false;
  selectedMfaAuthType: string = 'none';
  isAuthEnabled: boolean = true;
  isAnyAuthFactorEnabled: boolean = true;
  preferenceBreadcrumbs: any = [
    {
      label: 'MFA Preferences',
      active: true,
    },
  ];
  constructor(
    private userService: UserService,
    private errorHandler: GlobalErrorHandlerService,
    private toast: ToastService
  ) {}

  ngOnInit() {
    this.getAuthPolicies();
    this.getGuardianFactors();
  }

  /**
   * Get guardian factor
   */
  getGuardianFactors() {
    this.loader = true;
    this.userService
      .getGuardianFactorApi()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          if (response) {
            if (response.length) {
              response.forEach((element) => {
                if (element.name == 'push-notification') {
                  this.guardianPushNotificationEnable = element.enabled;
                } else if (element.name == 'otp') {
                  this.authenticatorOtpEnable = element.enabled;
                } else if (element.name == 'sms') {
                  this.smsEnable = element.enabled;
                } else if (element.name == 'recovery-code') {
                  this.recoveryCodeEnable = element.enabled;
                } else if (element.name == 'email') {
                  this.emailEnable = element.enabled;
                }
              });
              this.checkAnyFactorEnabled();
            }
          }
          this.loader = false;
        },
        (err) => {
          this.loader = false;
          this.errorHandler.apiErrorHandle(err);
        }
      );
  }

  /**
   * Check any one of the auth factor enabled
   */
  checkAnyFactorEnabled(checkFactor?) {
    if (
      this.guardianPushNotificationEnable ||
      this.authenticatorOtpEnable ||
      this.smsEnable ||
      this.recoveryCodeEnable ||
      this.emailEnable
    ) {
      this.isAnyAuthFactorEnabled = true;
    } else {
      this.isAnyAuthFactorEnabled = false;
      if (checkFactor) {
        this.toast.warning(
          'Please select any one of the auth factors (from primary or secondary mode) to prompt MFA, to avoid any issues.'
        );
      }
    }
  }

  /**
   * Get Auth policies
   */
  getAuthPolicies() {
    this.userService
      .getAuthPolicyApi()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          if (response.length && response[0] == 'confidence-score') {
            this.selectedMfaAuthType = 'confidence-score';
            this.isAuthEnabled = true;
          } else if (response.length && response[0] == 'all-applications') {
            this.selectedMfaAuthType = 'all-applications';
            this.isAuthEnabled = true;
          } else {
            this.selectedMfaAuthType = 'none';
            this.isAuthEnabled = false;
          }
        },
        (err) => {
          this.loader = false;
          this.errorHandler.apiErrorHandle(err);
        }
      );
  }

  /**
   * submit auth required policy
   */
  requireAuthClick(type: any) {
    let body: any = [];
    if (type) {
      body.push(type);
    }
    this.loader = true;
    this.userService
      .updateAuthPolicies(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          if (response.length && response[0] == 'confidence-score') {
            this.selectedMfaAuthType = 'confidence-score';
            this.isAuthEnabled = true;
            this.checkAnyFactorEnabled(true);
          } else if (response.length && response[0] == 'all-applications') {
            this.selectedMfaAuthType = 'all-applications';
            this.isAuthEnabled = true;
            this.checkAnyFactorEnabled(true);
          } else {
            this.selectedMfaAuthType = 'none';
            this.isAuthEnabled = false;
          }
          this.loader = false;
        },
        (err) => {
          this.loader = false;
          this.errorHandler.apiErrorHandle(err);
        }
      );
  }

  /**
   * Submit auth factor
   */
  onActionClick(type, enabled) {
    this.loader = true;
    let body: any = {
      enabled: enabled,
    };
    this.userService
      .updateGuardian(type, body)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          let toastMsg: string = '';
          if (type == 'push-notification') {
            this.guardianPushNotificationEnable = response.enabled;
            toastMsg = `Push Notification MFA is now ${response.enabled ? 'enabled' : 'disabled'}`;
          } else if (type == 'otp') {
            this.authenticatorOtpEnable = response.enabled;
            toastMsg = `OTP MFA is now ${response.enabled ? 'enabled' : 'disabled'}`;
          } else if (type == 'sms') {
            this.smsEnable = response.enabled;
            toastMsg = `SMS MFA is now ${response.enabled ? 'enabled' : 'disabled'}`;
          } else if (type == 'recovery-code') {
            this.recoveryCodeEnable = response.enabled;
            toastMsg = `Recovery code MFA is now ${response.enabled ? 'enabled' : 'disabled'}`;
          } else if (type == 'email') {
            this.emailEnable = response.enabled;
            toastMsg = `Email MFA is now ${response.enabled ? 'enabled' : 'disabled'}`;
          }
          this.toast.success(toastMsg);

          this.checkAnyFactorEnabled(true);
          this.loader = false;
        },
        (err) => {
          this.loader = false;
          this.toast.error('Failed to update MFA factors, Please try again');
          this.errorHandler.apiErrorHandle(err);
        }
      );
  }
}
